/**
 * Module dependencies.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Title } from 'app/components/atoms/title/title';
import { Spinner } from 'app/components/ui/spinner';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Checkbox } from 'app/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { CheckCircle2, ChevronDown, Copy, HelpCircle, Plug, Plus, Trash2, Wand2, XCircle } from 'lucide-react';
import { cn } from 'app/lib/utils';
import { Badge } from 'app/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from 'app/components/ui/dialog';
import {
  CryptoBotConfigUpdate,
  useCryptoBotConfig,
  useCryptoBotExchangeFilters,
  useImportBinanceKeys,
  useTestBinanceConnection,
  useUpdateCryptoBotConfig
} from 'app/hooks/requests/crypto-bot/use-crypto-bot';
import { CryptoBotBacktest } from './crypto-bot-backtest';

/**
 * Assets the user can add to the traded-pairs list.
 */

const ASSET_OPTIONS = [
  'BTC',
  'ETH',
  'SOL',
  'BNB',
  'XRP',
  'ADA',
  'DOGE',
  'AVAX',
  'LINK',
  'DOT',
  'MATIC',
  'LTC',
  'TRX',
  'ATOM',
  'UNI',
  'APT',
  'ARB',
  'OP',
  'NEAR',
  'FIL'
];

/**
 * Per-strategy tunable parameters, shown as a sub-form under the selected
 * strategy. Keys map to `strategyParams`; labels/hints come from i18n.
 */

const STRATEGY_PARAMS: Record<string, { key: string; def: number }[]> = {
  ema_crossover: [
    { key: 'fast', def: 12 },
    { key: 'slow', def: 26 }
  ],
  rsi_reversion: [
    { key: 'rsiPeriod', def: 14 },
    { key: 'oversold', def: 30 },
    { key: 'overbought', def: 70 }
  ],
  breakout: [{ key: 'lookback', def: 20 }],
  grid: [{ key: 'stepPct', def: 2 }],
  dca: [],
  rebalance: [
    { key: 'targetPct', def: 50 },
    { key: 'bandPct', def: 5 }
  ],
  rebalance_multi: [
    { key: 'cryptoPct', def: 50 },
    { key: 'bandPct', def: 5 }
  ],
  ema3: [
    { key: 'fast', def: 7 },
    { key: 'mid', def: 25 },
    { key: 'slow', def: 99 },
    { key: 'buyPct', def: 100 }
  ]
};

/**
 * Recommended risk presets per candle interval. Lower timeframes get tighter
 * risk and bigger R-multiples (to clear fees); higher timeframes can risk a bit
 * more with wider daily-loss room. Filled into the form, not auto-saved.
 */

type RiskPreset = {
  riskPerTradePct: number;
  maxOpenPositions: number;
  maxDailyLossPct: number;
  stopLossAtrMult: number;
  takeProfitR: number;
};

const RISK_PRESETS: Record<string, RiskPreset> = {
  '1m': { riskPerTradePct: 0.5, maxOpenPositions: 2, maxDailyLossPct: 3, stopLossAtrMult: 2, takeProfitR: 3 },
  '5m': { riskPerTradePct: 0.5, maxOpenPositions: 3, maxDailyLossPct: 4, stopLossAtrMult: 2, takeProfitR: 3 },
  '15m': { riskPerTradePct: 1, maxOpenPositions: 3, maxDailyLossPct: 5, stopLossAtrMult: 2, takeProfitR: 2.5 },
  '30m': { riskPerTradePct: 1, maxOpenPositions: 3, maxDailyLossPct: 5, stopLossAtrMult: 2, takeProfitR: 2 },
  '1h': { riskPerTradePct: 1, maxOpenPositions: 3, maxDailyLossPct: 5, stopLossAtrMult: 1.5, takeProfitR: 2 },
  '4h': { riskPerTradePct: 1.5, maxOpenPositions: 4, maxDailyLossPct: 6, stopLossAtrMult: 1.5, takeProfitR: 2.5 },
  '12h': { riskPerTradePct: 2, maxOpenPositions: 4, maxDailyLossPct: 8, stopLossAtrMult: 1.5, takeProfitR: 3 },
  '1d': { riskPerTradePct: 2, maxOpenPositions: 5, maxDailyLossPct: 10, stopLossAtrMult: 1.5, takeProfitR: 3 }
};

/**
 * Local editable form state.
 */

type FormState = {
  mode: 'dry' | 'live';
  quoteAsset: string;
  baseAssets: string[];
  strategy: string;
  candleInterval: string;
  riskPerTradePct: string;
  maxOpenPositions: string;
  maxDailyLossPct: string;
  stopLossAtrMult: string;
  takeProfitR: string;
};

/**
 * Format an ISO/MySQL datetime for the "last saved" label.
 */

function formatSavedAt(value?: string | null): string | null {
  if (!value) {
    return null;
  }

  const date = new Date(value.replace(' ', 'T'));

  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
}

/**
 * When the bot is running, settings are read-only. Sections read this to lock
 * their fields (the collapse toggle stays usable so you can still view them).
 */

const SettingsLockContext = React.createContext(false);

/**
 * A collapsible settings section — title + explanatory blurb + a "last saved"
 * date. Starts open unless `defaultCollapsed` is set (e.g. an already-configured
 * card that should be tucked away on reload).
 */

function Section({
  id,
  title,
  description,
  savedAt,
  defaultCollapsed,
  children
}: {
  id: string;
  title: string;
  description: string;
  savedAt?: string | null;
  defaultCollapsed?: boolean;
  children: React.ReactNode;
}) {
  const [translate] = useTranslation();
  const locked = React.useContext(SettingsLockContext);
  const storageKey = `cryptoBot.settings.collapse.${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const stored = window.localStorage.getItem(storageKey);

      if (stored === '1') {
        return true;
      }

      if (stored === '0') {
        return false;
      }
    } catch {
      // ignore storage errors
    }

    return !defaultCollapsed;
  });
  const saved = formatSavedAt(savedAt);

  const toggle = () =>
    setOpen(current => {
      const next = !current;

      try {
        window.localStorage.setItem(storageKey, next ? '1' : '0');
      } catch {
        // ignore storage errors
      }

      return next;
    });

  return (
    <section className={'rounded-xl border border-border bg-card shadow-sm'}>
      <button
        aria-expanded={open}
        className={'flex w-full items-center gap-3 px-5 py-4 text-left'}
        onClick={toggle}
        type={'button'}
      >
        <div className={'min-w-0 flex-1'}>
          <h2 className={'text-sm font-semibold uppercase tracking-wide text-foreground'}>{title}</h2>

          <p className={'mt-1 text-xs text-muted-foreground'}>{description}</p>

          {saved ? (
            <p className={'mt-1 text-[11px] font-medium text-emerald-600'}>
              {translate('cryptoBot.settings.lastSaved', { date: saved })}
            </p>
          ) : null}
        </div>

        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-300',
            open && 'rotate-180'
          )}
        />
      </button>

      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-in-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className={'overflow-hidden'}>
          <fieldset
            className={cn('m-0 flex min-w-0 flex-col gap-4 border-0 p-0 px-5 pb-5', locked && 'opacity-60')}
            disabled={locked}
          >
            {children}
          </fieldset>
        </div>
      </div>
    </section>
  );
}

/**
 * A labelled field — label + per-field hint.
 */

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className={'space-y-1'}>
      <label className={'text-xs font-medium text-foreground'}>{label}</label>

      {children}

      {hint ? <div className={'text-[11px] text-muted-foreground'}>{hint}</div> : null}
    </div>
  );
}

/**
 * Export `CryptoBotSettings` component.
 */

export function CryptoBotSettings(): JSX.Element {
  const [translate] = useTranslation();
  const { success, error } = useToast();
  const { data: config, isLoading } = useCryptoBotConfig();
  const update = useUpdateCryptoBotConfig();
  const testConnection = useTestBinanceConnection();
  const importKeys = useImportBinanceKeys();
  const { data: exchangeFilters } = useCryptoBotExchangeFilters();

  const [form, setForm] = useState<FormState | null>(null);
  const [params, setParams] = useState<Record<string, string>>({});
  const [pick, setPick] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    error?: string;
    quoteAsset?: string;
    quoteBalance?: number;
    assets?: number;
    balances?: { asset: string; amount: number }[];
    permissions?: { reading: boolean; spotTrading: boolean; withdrawals: boolean; ipRestrict: boolean } | null;
  } | null>(null);

  useEffect(() => {
    if (config && !form) {
      setForm({
        mode: config.mode,
        quoteAsset: config.quoteAsset,
        baseAssets: [...config.baseAssets],
        strategy: config.strategy,
        candleInterval: config.candleInterval,
        riskPerTradePct: String(config.riskPerTradePct),
        maxOpenPositions: String(config.maxOpenPositions),
        maxDailyLossPct: String(config.maxDailyLossPct),
        stopLossAtrMult: String(config.stopLossAtrMult),
        takeProfitR: String(config.takeProfitR)
      });

      const saved = config.strategyParams || {};

      setParams(Object.fromEntries(Object.entries(saved).map(([key, value]) => [key, String(value)])));
    }
  }, [config, form]);

  if (isLoading || !config || !form) {
    return (
      <div className={'grid place-items-center py-20'}>
        <Spinner />
      </div>
    );
  }

  const set = (patch: Partial<FormState>) => setForm(current => ({ ...(current as FormState), ...patch }));
  const available = ASSET_OPTIONS.filter(asset => !form.baseAssets.includes(asset));

  const addPair = () => {
    if (pick && !form.baseAssets.includes(pick)) {
      set({ baseAssets: [...form.baseAssets, pick] });
      setPick('');
    }
  };

  const removePair = (asset: string) => set({ baseAssets: form.baseAssets.filter(item => item !== asset) });

  const saveSection = async (section: string, patch: CryptoBotConfigUpdate) => {
    try {
      await update.mutateAsync({ ...patch, section });
      success({ message: translate('cryptoBot.settings.saved') });
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? 'Error' });
    }
  };

  const saveMarket = () =>
    saveSection('market', { mode: form.mode, quoteAsset: form.quoteAsset, candleInterval: form.candleInterval });

  const savePairs = () => saveSection('pairs', { baseAssets: form.baseAssets });

  const saveStrategy = () => {
    const strategyParams: Record<string, number> = {};

    for (const { key, def } of STRATEGY_PARAMS[form.strategy] ?? []) {
      const raw = params[key];

      strategyParams[key] = raw === undefined || raw === '' ? def : Number(raw);
    }

    return saveSection('strategy', { strategy: form.strategy, strategyParams } as CryptoBotConfigUpdate);
  };

  const saveRisk = () =>
    saveSection('risk', {
      riskPerTradePct: Number(form.riskPerTradePct),
      maxOpenPositions: Number(form.maxOpenPositions),
      maxDailyLossPct: Number(form.maxDailyLossPct),
      stopLossAtrMult: Number(form.stopLossAtrMult),
      takeProfitR: Number(form.takeProfitR)
    });

  const applyRiskPreset = () => {
    const preset = RISK_PRESETS[form.candleInterval] ?? RISK_PRESETS['1h'];

    set({
      riskPerTradePct: String(preset.riskPerTradePct),
      maxOpenPositions: String(preset.maxOpenPositions),
      maxDailyLossPct: String(preset.maxDailyLossPct),
      stopLossAtrMult: String(preset.stopLossAtrMult),
      takeProfitR: String(preset.takeProfitR)
    });
  };

  const onSaveKeys = async () => {
    if (!apiKey.trim() || !apiSecret.trim()) {
      error({ message: translate('cryptoBot.settings.binanceMissing') });

      return;
    }

    try {
      await update.mutateAsync({ binanceApiKey: apiKey.trim(), binanceApiSecret: apiSecret.trim() });
      setApiKey('');
      setApiSecret('');
      setTestResult(null);
      success({ message: translate('cryptoBot.settings.binanceSaved') });
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? 'Error' });
    }
  };

  const onDisconnect = async () => {
    try {
      await update.mutateAsync({ disconnectBinance: true });
      setTestResult(null);
      success({ message: translate('cryptoBot.settings.binanceDisconnected') });
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? 'Error' });
    }
  };

  const onTest = async () => {
    try {
      const result = await testConnection.mutateAsync();

      setTestResult(result);
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? 'Error' });
    }
  };

  const onImportKeys = async () => {
    try {
      const result = await importKeys.mutateAsync();

      if (result?.ok) {
        setTestResult(null);
        success({ message: translate('cryptoBot.settings.binanceImported') });
      } else {
        error({ message: translate('cryptoBot.settings.binanceImportNone') });
      }
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? 'Error' });
    }
  };

  const isRunning = !!config.enabled;

  return (
    <SettingsLockContext.Provider value={isRunning}>
      <div className={'w-full max-w-2xl space-y-6'}>
        <Title>{translate('cryptoBot.settings.title')}</Title>

        {isRunning ? (
          <div
            className={
              'rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200'
            }
          >
            {translate('cryptoBot.settings.lockedNotice')}
          </div>
        ) : null}

        {/* Binance connection — first; collapsed once configured */}
        <Section
          defaultCollapsed={config.hasBinanceKeys}
          description={translate('cryptoBot.settings.binanceDesc')}
          id={'binance'}
          savedAt={config.binanceKeysUpdatedAt}
          title={translate('cryptoBot.settings.binance')}
        >
          <div className={'flex items-center gap-2'}>
            {config.hasBinanceKeys ? (
              <Badge className={'gap-1'} variant={'default'}>
                <CheckCircle2 className={'h-3 w-3'} />
                {translate('cryptoBot.settings.binanceConnected')}
              </Badge>
            ) : (
              <Badge className={'gap-1'} variant={'secondary'}>
                <XCircle className={'h-3 w-3'} />
                {translate('cryptoBot.settings.binanceNotConnected')}
              </Badge>
            )}

            {config.binanceKeyMasked ? (
              <span className={'font-mono text-xs text-muted-foreground'}>{config.binanceKeyMasked}</span>
            ) : null}

            {config.binanceSource && config.binanceSource !== 'own' ? (
              <span className={'text-xs text-muted-foreground'}>
                {translate(`cryptoBot.settings.binanceVia.${config.binanceSource}`)}
              </span>
            ) : null}
          </div>

          <div className={'grid gap-4 sm:grid-cols-2'}>
            <Field
              hint={translate('cryptoBot.settings.binanceApiKeyHint')}
              label={translate('cryptoBot.settings.binanceApiKey')}
            >
              <Input
                autoComplete={'off'}
                onChange={e => setApiKey(e.target.value)}
                placeholder={config.hasBinanceKeys ? '••••••••••••' : ''}
                value={apiKey}
              />
            </Field>

            <Field
              hint={translate('cryptoBot.settings.binanceApiSecretHint')}
              label={translate('cryptoBot.settings.binanceApiSecret')}
            >
              <Input
                autoComplete={'off'}
                onChange={e => setApiSecret(e.target.value)}
                placeholder={config.hasBinanceKeys ? '••••••••••••' : ''}
                type={'password'}
                value={apiSecret}
              />
            </Field>
          </div>

          <div
            className={
              'rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200'
            }
          >
            {translate('cryptoBot.settings.binanceSecurity')}
          </div>

          <div className={'flex flex-wrap items-center gap-2'}>
            <Button
              disabled={update.isLoading || !apiKey.trim() || !apiSecret.trim()}
              onClick={onSaveKeys}
              type={'button'}
            >
              {translate('cryptoBot.settings.binanceSave')}
            </Button>

            {config.binanceImportable ? (
              <Button disabled={importKeys.isLoading} onClick={onImportKeys} type={'button'} variant={'outline'}>
                <Copy className={'h-4 w-4'} />
                {translate('cryptoBot.settings.binanceImport')}
              </Button>
            ) : null}

            <Button
              disabled={testConnection.isLoading || !config.hasBinanceKeys}
              onClick={onTest}
              type={'button'}
              variant={'outline'}
            >
              <Plug className={'h-4 w-4'} />
              {translate('cryptoBot.settings.binanceTest')}
            </Button>

            {config.hasBinanceKeys ? (
              <Button
                className={'text-destructive hover:text-destructive'}
                disabled={update.isLoading}
                onClick={onDisconnect}
                type={'button'}
                variant={'ghost'}
              >
                {translate('cryptoBot.settings.binanceDisconnect')}
              </Button>
            ) : null}
          </div>

          {testResult ? (
            testResult.ok ? (
              <div className={'space-y-2'}>
                <div className={'flex items-center gap-2 text-sm text-emerald-600'}>
                  <CheckCircle2 className={'h-4 w-4'} />
                  {translate('cryptoBot.settings.binanceTestOk', {
                    balance: (testResult.quoteBalance ?? 0).toLocaleString('en-US', { maximumFractionDigits: 2 }),
                    quote: testResult.quoteAsset,
                    assets: testResult.assets
                  })}
                </div>

                {testResult.balances?.length ? (
                  <details className={'overflow-hidden rounded-lg border border-border'}>
                    <summary
                      className={
                        'cursor-pointer select-none px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-muted/40'
                      }
                    >
                      {translate('cryptoBot.settings.showAssets', { count: testResult.balances.length })}
                    </summary>

                    <table className={'w-full border-collapse border-t border-border text-sm'}>
                      <tbody className={'divide-y divide-border'}>
                        {testResult.balances.map(balance => (
                          <tr className={'hover:bg-muted/40'} key={balance.asset}>
                            <td className={'px-3 py-1.5 font-medium text-foreground'}>{balance.asset}</td>

                            <td className={'px-3 py-1.5 text-right tabular-nums text-muted-foreground'}>
                              {balance.amount.toLocaleString('en-US', { maximumFractionDigits: 8 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                ) : null}

                {testResult.permissions ? (
                  <div className={'space-y-1 rounded-lg border border-border p-3'}>
                    <div className={'text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'}>
                      {translate('cryptoBot.settings.binancePermissions')}
                    </div>

                    <div className={'flex items-center gap-2 text-sm'}>
                      {testResult.permissions.reading ? (
                        <CheckCircle2 className={'h-4 w-4 text-emerald-600'} />
                      ) : (
                        <XCircle className={'h-4 w-4 text-destructive'} />
                      )}
                      <span>{translate('cryptoBot.settings.permReading')}</span>
                    </div>

                    <div
                      className={cn(
                        'flex items-center gap-2 text-sm',
                        !testResult.permissions.spotTrading && 'text-destructive'
                      )}
                    >
                      {testResult.permissions.spotTrading ? (
                        <CheckCircle2 className={'h-4 w-4 text-emerald-600'} />
                      ) : (
                        <XCircle className={'h-4 w-4 text-destructive'} />
                      )}
                      <span>{translate('cryptoBot.settings.permSpotTrading')}</span>
                    </div>

                    {!testResult.permissions.spotTrading ? (
                      <div className={'text-[11px] text-destructive'}>
                        {translate('cryptoBot.settings.permSpotTradingHint')}
                      </div>
                    ) : null}

                    {testResult.permissions.ipRestrict ? (
                      <div className={'text-[11px] text-muted-foreground'}>
                        {translate('cryptoBot.settings.permIpRestrict')}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className={'flex items-center gap-2 text-sm text-destructive'}>
                <XCircle className={'h-4 w-4'} />
                {testResult.error === 'no-credentials'
                  ? translate('cryptoBot.settings.binanceTestNoCreds')
                  : translate('cryptoBot.settings.binanceTestFail', { error: testResult.error })}
              </div>
            )
          ) : null}
        </Section>

        {/* Pairs */}
        <Section
          defaultCollapsed={!!config.sectionsSavedAt?.pairs}
          description={translate('cryptoBot.settings.pairsDesc')}
          id={'pairs'}
          savedAt={config.sectionsSavedAt?.pairs}
          title={translate('cryptoBot.settings.pairs')}
        >
          <div className={'flex items-end gap-2'}>
            <div className={'flex-1'}>
              <Select onValueChange={setPick} value={pick || undefined}>
                <SelectTrigger className={'w-full'}>
                  <SelectValue placeholder={translate('cryptoBot.settings.pairsSelect')} />
                </SelectTrigger>

                <SelectContent>
                  {available.map(asset => (
                    <SelectItem key={asset} value={asset}>
                      {asset}
                      {'/'}
                      {form.quoteAsset}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button disabled={!pick} onClick={addPair} type={'button'} variant={'outline'}>
              <Plus className={'h-4 w-4'} />
              {translate('cryptoBot.settings.pairsAdd')}
            </Button>
          </div>

          {form.baseAssets.length ? (
            <div className={'overflow-hidden rounded-lg border border-border'}>
              <table className={'w-full border-collapse text-sm'}>
                <tbody className={'divide-y divide-border'}>
                  {form.baseAssets.map(asset => (
                    <tr className={'hover:bg-muted/40'} key={asset}>
                      <td className={'px-3 py-2 font-medium text-foreground'}>
                        {asset}
                        {'/'}
                        {form.quoteAsset}
                      </td>

                      <td className={'px-3 py-2 text-right'}>
                        <Button
                          className={'h-7 w-7 text-destructive hover:text-destructive'}
                          onClick={() => removePair(asset)}
                          size={'icon'}
                          type={'button'}
                          variant={'ghost'}
                        >
                          <Trash2 className={'h-4 w-4'} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div
              className={
                'rounded-lg border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground'
              }
            >
              {translate('cryptoBot.settings.pairsEmpty')}
            </div>
          )}

          <div className={'flex justify-end'}>
            <Button disabled={update.isLoading} onClick={savePairs} type={'button'}>
              {translate('cryptoBot.settings.save')}
            </Button>
          </div>
        </Section>

        {/* Exchange limits — deterministic, read-only (fetched from Binance) */}
        <Section
          defaultCollapsed
          description={translate('cryptoBot.settings.limitsDesc')}
          id={'limits'}
          title={translate('cryptoBot.settings.limits')}
        >
          {exchangeFilters && exchangeFilters.length > 0 ? (
            <div className={'overflow-hidden rounded-lg border border-border'}>
              <table className={'w-full border-collapse text-sm'}>
                <thead>
                  <tr className={'border-b border-border bg-muted/40 text-[11px] uppercase text-muted-foreground'}>
                    <th className={'px-3 py-2 text-left font-medium'}>{translate('cryptoBot.settings.limitsPair')}</th>
                    <th className={'px-3 py-2 text-right font-medium'}>
                      {translate('cryptoBot.settings.limitsMinNotional')}
                    </th>
                    <th className={'px-3 py-2 text-right font-medium'}>
                      {translate('cryptoBot.settings.limitsMinQty')}
                    </th>
                    <th className={'px-3 py-2 text-right font-medium'}>{translate('cryptoBot.settings.limitsStep')}</th>
                  </tr>
                </thead>

                <tbody className={'divide-y divide-border'}>
                  {exchangeFilters.map(filter => (
                    <tr className={'hover:bg-muted/40'} key={filter.symbol}>
                      <td className={'px-3 py-1.5 font-medium text-foreground'}>
                        {filter.asset}
                        {'/'}
                        {filter.quote}
                      </td>

                      <td className={'px-3 py-1.5 text-right tabular-nums text-muted-foreground'}>
                        {filter.minNotional != null ? `${filter.minNotional} ${filter.quote}` : '—'}
                      </td>

                      <td className={'px-3 py-1.5 text-right tabular-nums text-muted-foreground'}>
                        {filter.minQty ?? '—'}
                      </td>

                      <td className={'px-3 py-1.5 text-right tabular-nums text-muted-foreground'}>
                        {filter.stepSize ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div
              className={
                'rounded-lg border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground'
              }
            >
              {translate('cryptoBot.settings.limitsEmpty')}
            </div>
          )}

          <p className={'text-[11px] text-muted-foreground'}>{translate('cryptoBot.settings.limitsNote')}</p>
        </Section>

        {/* Strategy */}
        <Section
          defaultCollapsed={!!config.sectionsSavedAt?.strategy}
          description={translate('cryptoBot.settings.strategyDesc')}
          id={'strategy'}
          savedAt={config.sectionsSavedAt?.strategy}
          title={translate('cryptoBot.settings.strategySection')}
        >
          <div className={'flex justify-end'}>
            <Dialog>
              <DialogTrigger asChild>
                <Button size={'sm'} type={'button'} variant={'outline'}>
                  <HelpCircle className={'h-4 w-4'} />
                  {translate('cryptoBot.settings.strategyHelp')}
                </Button>
              </DialogTrigger>

              <DialogContent className={'max-w-lg'}>
                <DialogHeader>
                  <DialogTitle>{translate('cryptoBot.settings.strategyHelpTitle')}</DialogTitle>

                  <DialogDescription>{translate('cryptoBot.settings.strategyHelpDesc')}</DialogDescription>
                </DialogHeader>

                <div className={'space-y-3'}>
                  {config.strategies.map(strategy => (
                    <div
                      className={cn(
                        'rounded-lg border p-3',
                        strategy === form.strategy ? 'border-primary bg-primary/5' : 'border-border'
                      )}
                      key={strategy}
                    >
                      <div className={'flex items-center gap-2'}>
                        <span className={'text-sm font-semibold text-foreground'}>
                          {translate(`cryptoBot.strategies.${strategy}`)}
                        </span>

                        {strategy === form.strategy ? (
                          <Badge className={'px-1.5 py-0 text-[10px] uppercase'} variant={'default'}>
                            {translate('cryptoBot.settings.strategyHelpSelected')}
                          </Badge>
                        ) : null}
                      </div>

                      <p className={'mt-1 text-xs text-muted-foreground'}>
                        {translate(`cryptoBot.strategyHints.${strategy}`)}
                      </p>
                    </div>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <Field
            hint={translate(`cryptoBot.strategyHints.${form.strategy}`)}
            label={translate('cryptoBot.settings.strategy')}
          >
            <Select onValueChange={value => set({ strategy: value })} value={form.strategy}>
              <SelectTrigger className={'w-full'}>
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {config.strategies.map(strategy => (
                  <SelectItem key={strategy} value={strategy}>
                    {translate(`cryptoBot.strategies.${strategy}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {(STRATEGY_PARAMS[form.strategy] ?? []).length ? (
            <div className={'space-y-3 rounded-lg border border-border bg-muted/30 p-3'}>
              <div className={'text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'}>
                {translate('cryptoBot.settings.strategyParams')}
              </div>

              <div className={'grid gap-4 sm:grid-cols-2'}>
                {(STRATEGY_PARAMS[form.strategy] ?? []).map(({ key, def }) => (
                  <Field
                    hint={translate(`cryptoBot.settings.paramHints.${key}`)}
                    key={key}
                    label={translate(`cryptoBot.settings.params.${key}`)}
                  >
                    <Input
                      onChange={e => setParams(current => ({ ...current, [key]: e.target.value }))}
                      placeholder={String(def)}
                      type={'number'}
                      value={params[key] ?? ''}
                    />
                  </Field>
                ))}
              </div>
            </div>
          ) : null}

          <div className={'flex justify-end'}>
            <Button disabled={update.isLoading} onClick={saveStrategy} type={'button'}>
              {translate('cryptoBot.settings.save')}
            </Button>
          </div>
        </Section>

        {/* Risk */}
        <Section
          defaultCollapsed={!!config.sectionsSavedAt?.risk}
          description={translate('cryptoBot.settings.riskDesc')}
          id={'risk'}
          savedAt={config.sectionsSavedAt?.risk}
          title={translate('cryptoBot.settings.risk')}
        >
          <div className={'flex justify-end'}>
            <Button onClick={applyRiskPreset} size={'sm'} type={'button'} variant={'outline'}>
              <Wand2 className={'h-4 w-4'} />
              {translate('cryptoBot.settings.riskRecommended', { interval: form.candleInterval })}
            </Button>
          </div>

          <div className={'grid gap-4 sm:grid-cols-2'}>
            <Field
              hint={translate('cryptoBot.settings.riskPerTradeHint')}
              label={translate('cryptoBot.settings.riskPerTradePct')}
            >
              <Input
                onChange={e => set({ riskPerTradePct: e.target.value })}
                type={'number'}
                value={form.riskPerTradePct}
              />
            </Field>

            <Field
              hint={translate('cryptoBot.settings.maxOpenHint')}
              label={translate('cryptoBot.settings.maxOpenPositions')}
            >
              <Input
                onChange={e => set({ maxOpenPositions: e.target.value })}
                type={'number'}
                value={form.maxOpenPositions}
              />
            </Field>

            <Field
              hint={translate('cryptoBot.settings.maxDailyLossHint')}
              label={translate('cryptoBot.settings.maxDailyLossPct')}
            >
              <Input
                onChange={e => set({ maxDailyLossPct: e.target.value })}
                type={'number'}
                value={form.maxDailyLossPct}
              />
            </Field>

            <Field
              hint={translate('cryptoBot.settings.stopHint')}
              label={translate('cryptoBot.settings.stopLossAtrMult')}
            >
              <Input
                onChange={e => set({ stopLossAtrMult: e.target.value })}
                type={'number'}
                value={form.stopLossAtrMult}
              />
            </Field>

            <Field
              hint={translate('cryptoBot.settings.takeProfitHint')}
              label={translate('cryptoBot.settings.takeProfitR')}
            >
              <Input onChange={e => set({ takeProfitR: e.target.value })} type={'number'} value={form.takeProfitR} />
            </Field>
          </div>

          <div className={'flex justify-end'}>
            <Button disabled={update.isLoading} onClick={saveRisk} type={'button'}>
              {translate('cryptoBot.settings.save')}
            </Button>
          </div>
        </Section>

        {/* Market */}
        <Section
          defaultCollapsed={!!config.sectionsSavedAt?.market}
          description={translate('cryptoBot.settings.marketDesc')}
          id={'market'}
          savedAt={config.sectionsSavedAt?.market}
          title={translate('cryptoBot.settings.market')}
        >
          <div className={'grid gap-4 sm:grid-cols-2'}>
            <Field hint={translate('cryptoBot.settings.modeHint')} label={translate('cryptoBot.settings.mode')}>
              <Select onValueChange={value => set({ mode: value as 'dry' | 'live' })} value={form.mode}>
                <SelectTrigger className={'w-full'}>
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value={'dry'}>{translate('cryptoBot.mode.dry')}</SelectItem>

                  <SelectItem value={'live'}>{translate('cryptoBot.mode.live')}</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field hint={translate('cryptoBot.settings.quoteHint')} label={translate('cryptoBot.settings.quoteAsset')}>
              <Select onValueChange={value => set({ quoteAsset: value })} value={form.quoteAsset}>
                <SelectTrigger className={'w-full'}>
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value={'USDC'}>{'USDC'}</SelectItem>

                  <SelectItem value={'USDT'}>{'USDT'}</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field
              hint={translate('cryptoBot.settings.intervalHint')}
              label={translate('cryptoBot.settings.candleInterval')}
            >
              <Select onValueChange={value => set({ candleInterval: value })} value={form.candleInterval}>
                <SelectTrigger className={'w-full'}>
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  {config.intervals.map(interval => (
                    <SelectItem key={interval} value={interval}>
                      {interval}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className={'flex justify-end'}>
            <Button disabled={update.isLoading} onClick={saveMarket} type={'button'}>
              {translate('cryptoBot.settings.save')}
            </Button>
          </div>
        </Section>

        {/* Backtest */}
        <Section
          description={translate('cryptoBot.settings.backtestDesc')}
          id={'backtest'}
          title={translate('cryptoBot.settings.backtest')}
        >
          <CryptoBotBacktest />
        </Section>

        {/* AI — milestone 2 */}
        <Section
          defaultCollapsed
          description={translate('cryptoBot.settings.aiDesc')}
          id={'ai'}
          title={translate('cryptoBot.settings.ai')}
        >
          <label className={'flex items-center gap-2 text-sm text-muted-foreground'}>
            <Checkbox checked={false} disabled />

            {translate('cryptoBot.settings.aiHint')}
          </label>
        </Section>
      </div>
    </SettingsLockContext.Provider>
  );
}
